//#region Imports Packages and Libs

import { InlineClickAction } from "./../../../types";
import { UrlUtils } from "./url_utils";
import { Util } from "./../../../util";

//#endregion

//#region ActionResolver Class

/**
 * The `ActionResolver` class provides utilities to extract action keys from URLs
 * and retrieve corresponding inline click actions from a predefined set.
 *
 */
export class ActionResolver {
	/**
	 * Extracts the action key from a URL by normalizing the URL and retrieving the "name" query parameter.
	 *
	 * @param url - The URL string to extract the action key from.
	 * @returns The value of the "name" query parameter if it exists, otherwise `null`.
	 */
	static getActionKeyFromUrl(url: string): string | null {
		const normalized = UrlUtils.normalizeUrl(url);
		return UrlUtils.extractQueryParam(normalized, "name");
	}

	/**
	 * Sanitizes a given HTML string by decoding URI components and escaping HTML special characters
	 * to prevent XSS attacks.
	 *
	 * @param value - The input string to sanitize.
	 * @returns The sanitized string with HTML special characters escaped.
	 *          Returns `null` if the input is invalid or an error occurs.
	 */
	static sanitizeHtml(value: string): string | null {
		try {
			if (typeof value !== "string" || value.trim() === "") return null;
			value = decodeURIComponent(value); // Decode URI component to handle encoded characters

			// Escape HTML special characters
			value = value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
			return value;
		} catch (ex) {
			return null;
		}
	}

	/**
	 * This method normalizes the provided URL, retrieves the value of the "reply_text"
	 * query parameter, sanitizes it to remove any potentially unsafe HTML, and returns
	 * the sanitized value.
	 *
	 * @param url - The URL string from which to extract the "reply_text" parameter.
	 * @returns The sanitized "reply_text" value if present and non-empty; otherwise, `null`.
	 */
	static getReplyTextFromUrl(url: string): string | null {
		try {
			const normalized = UrlUtils.normalizeUrl(url);
			let value = UrlUtils.extractQueryParam(normalized, "reply_text");
			value = this.sanitizeHtml(value);
			if (!value || value.trim() === "") return null;
			return value;
		} catch (ex) {
			//console.log("[ActionResolver] Error extracting reply text from URL:\n", ex);
			return null;
		}
	}

	/**
	 * Retrieves an inline click action associated with the specified key.
	 *
	 * @param key - The key used to identify the desired inline click action.
	 * @returns The corresponding `InlineClickAction` if found; otherwise, `null`.
	 */
	static getActionByKey(key: string, clickActions: Record<string, InlineClickAction>): InlineClickAction | null {
		if (typeof key !== "string" || key.trim() === "") return null;
		if (!clickActions || !Util.propertyHasValue(clickActions, key)) return null;
		return clickActions[key] || null;
	}
}

//#endregion
